'use client';

import React, { memo, useId } from 'react';

interface AppLogoProps {
  /** Rendered width and height in pixels. The mark is square. */
  size?: number;
  className?: string;
  onClick?: () => void;
}

/**
 * The Quantifyr mark: a step, in two pieces.
 *
 * The lower piece is where the business was, the upper one is where it is now.
 * That is the whole product in one shape — every figure on every screen is
 * this week measured against last, and a step is what that looks like when you
 * draw it. It climbs in a stair rather than an arrow because progress here
 * arrives one week at a time, and an arrow would promise a smoothness the data
 * never has.
 *
 * The two pieces touch. An earlier version separated them with a gap so the
 * upper one could cast a shadow across it, and that read as two loose objects
 * rather than one silhouette. The depth now comes from shading pooled in the
 * inner corner — where one piece rests on the other and light cannot reach —
 * plus a highlight along the top edge. Both are how light actually behaves,
 * which is why they survive being shrunk.
 *
 * Colours are fixed rather than drawn from the theme tokens. A logo should be
 * the same object on every surface, and the three-stop gradient is tuned as a
 * whole; letting the theme rewrite individual stops would flatten it. The
 * greens sit far enough from both canvases to hold their contrast on either.
 *
 * The favicon is a different drawing (`public/favicon.svg`) — see the note in
 * that file for why gradients are no use at 16px.
 */
const AppLogo = memo(function AppLogo({ size = 32, className = '', onClick }: AppLogoProps) {
  // Every instance needs its own gradient ids. The sidebar alone renders two,
  // and duplicate ids in one document are invalid — the browser resolves every
  // reference to whichever came first, so a later change to one instance would
  // silently fail to appear.
  const uid = useId().replace(/:/g, '');

  const classes = [
    'flex-shrink-0',
    onClick ? 'cursor-pointer transition-opacity hover:opacity-80' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const lower = 'M15 47 H48 V60 A5 5 0 0 1 43 65 H15 A5 5 0 0 1 10 60 V52 A5 5 0 0 1 15 47 Z';
  const upper =
    'M35 11 H61 A5 5 0 0 1 66 16 V24 A5 5 0 0 1 61 29 H53 A5 5 0 0 0 48 34 V48 H30 V16 A5 5 0 0 1 35 11 Z';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 76 76"
      fill="none"
      className={classes}
      onClick={onClick}
      role="img"
      aria-label="Quantifyr"
    >
      <defs>
        <linearGradient
          id={`${uid}lo`}
          x1="10"
          y1="47"
          x2="44"
          y2="65"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#13855f" />
          <stop offset="1" stopColor="#0a6247" />
        </linearGradient>
        <linearGradient
          id={`${uid}up`}
          x1="66"
          y1="11"
          x2="30"
          y2="48"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#8bf8cd" />
          <stop offset="0.5" stopColor="#1ad796" />
          <stop offset="1" stopColor="#10b47c" />
        </linearGradient>
        {/* Contact shading, centred on the inner corner. */}
        <radialGradient id={`${uid}ao`} cx="30" cy="47" r="17" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#01100a" stopOpacity="0.42" />
          <stop offset="1" stopColor="#01100a" stopOpacity="0" />
        </radialGradient>
        <linearGradient
          id={`${uid}rim`}
          x1="0"
          y1="47"
          x2="0"
          y2="52.5"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#6ff0b8" stopOpacity="0.45" />
          <stop offset="1" stopColor="#6ff0b8" stopOpacity="0" />
        </linearGradient>
        {/* Clipped against the drawn path itself, not an approximation of it —
            an earlier version clipped to an unstroked outline while the shape
            was drawn with a stroke, and the shading stopped short of the edge
            all the way round. */}
        <clipPath id={`${uid}clip`}>
          <path d={lower} />
        </clipPath>
      </defs>

      <path d={lower} fill={`url(#${uid}lo)`} />
      <g clipPath={`url(#${uid}clip)`}>
        <rect x="10" y="47" width="38" height="6" fill={`url(#${uid}rim)`} />
        <rect x="10" y="47" width="38" height="18" fill={`url(#${uid}ao)`} />
      </g>
      <path d={upper} fill={`url(#${uid}up)`} />
    </svg>
  );
});

export default AppLogo;
